import { editor, languages, Position, MarkerSeverity } from 'monaco-editor-core';
import { SqliteValidator } from './sqlite.validator';
import { modelOptionsSchema } from './config.validator';
import { SQLITE_ID } from './constants';
import { SqliteModelOptions, SqliteModelOptionsMap, SqliteModelParsedOptions } from './interface';
import * as Parser from './sqlite.parser';
import { LanguageService } from '../../interface';

export class SqliteLanguageService implements LanguageService {
  private static instance?: SqliteLanguageService;
  private modelOptions: SqliteModelOptionsMap = {};
  private validation: SqliteValidator;

  static getInstance(): SqliteLanguageService {
    if (!this.instance) {
      this.instance = new SqliteLanguageService();
    }
    return this.instance;
  }

  // singleton
  private constructor() {
    this.validation = SqliteValidator.getInstance();
  }

  public registerModelOptions(model: editor.ITextModel, options: SqliteModelOptions) {
    const { value, error } = modelOptionsSchema.validate(options);
    if (error) {
      throw error;
    }

    this.modelOptions[model.uri.toString()] = value;
  }

  public unregisterModelOptions(model: editor.ITextModel) {
    delete this.modelOptions[model.uri.toString()];
  }

  private getOptions(model: editor.ITextModel): SqliteModelParsedOptions {
    let options = this.modelOptions[model.uri.toString()];
    if (!options) {
      options = {
        validate: true,
        hover: false,
        autoComplete: false,
      };
    }

    return options;
  }

  public async doValidate(model: editor.ITextModel) {
    const options = this.getOptions(model);
    if (!options.validate || !options.schema) {
      return true;
    }

    const markers: editor.IMarkerData[] = [];
    let value = '';
    try {
      value = model.getValue();
      const ast = Parser.parse(value, {});
    } catch (error) {
      const lines = value.split('\r');
      let endColumn = error.location.end.column;
      if (error.location.start.column === endColumn - 1) {
        const startErrorString = lines[error.location.start.line - 1].substr(error.location.start.column);

        endColumn = startErrorString.search('[ \t]');
        if (endColumn === -1) {
          endColumn = lines[error.location.start.line - 1].length + 1;
        } else {
          endColumn = error.location.start.column + endColumn + 1;
        }
      }
      markers.push({
        severity: MarkerSeverity.Error,
        startLineNumber: error.location.start.line,
        startColumn: error.location.start.column,
        endLineNumber: error.location.end.line,
        endColumn: endColumn,
        message: options.schema.syntaxError,
      });
    }

    editor.setModelMarkers(model, SQLITE_ID, markers);
    return markers.length === 0;
  }

  public async provideHover(model: editor.ITextModel, position: Position): Promise<languages.Hover | null> {
    const options = this.getOptions(model);
    if (!options.hover) {
      return null;
    }

    const items = [];

    return { contents: items };
  }

  public parse(value: string) {
    return Parser.parse(value);
  }
}
